// =============================================================================
// domain/economics.ts — the Prop Report's Economics tab.
// -----------------------------------------------------------------------------
// "Has this business made money?" — real cash in against real cash out. The
// counterpart to Health, which asks "am I safe right now?".
//
// ── 🚨 A THIRD POPULATION, AND IT IS NOT HEALTH'S (D77) ──
//
// Health buckets with `classifyPropAccount`, where STATUS beats TYPE — a blown
// funded account is `blown`, so it contributes no buying power and no cushion.
// Economics ignores status entirely and buckets on TYPE alone, because a blown
// funded account still COST what it cost and still counts as a funded account
// reached. On Josh's book the two disagree out loud:
//
//   Health     0 funded · 8 blown
//   Economics  2 funded · 6 failed evals
//
// Both are right about their own question. The two funded accounts are blown, so
// Health gives them no cushion; Economics counts them because cost-per-funded is
// "what did it cost to reach a funded account", and reaching one you later blew
// still counts as reaching it.
//
// ⚠ `sim-funded` is an EVAL here and FUNDED in `classifyPropAccount`. Same field,
// two meanings, both deliberate. This is exactly why D77 forbids a shared
// population helper.
//
// ── LIFETIME, AND FULL HISTORY ──
//
// No date window: "a period selector would let you pick the window where you
// happened to be up". And never attempt-scoped — a reset changes what COMPLIANCE
// measures, not what the account cost you. Cost-per-funded depends on counting
// every attempt, including the failures.
//
// ── ⚠ ARCHIVED ACCOUNTS ARE INCLUDED, DELIBERATELY ──
//
// Archiving is visual de-clutter. A failed eval you archived is precisely the
// spend that ROI must account for; omitting it would flatter the number by
// hiding your losses. Archive DOES stop recurring-burn accrual — a dead eval
// should not bill forever — and that is the only thing it changes here.
// =============================================================================

import type { AccountTrade } from './account';
// ⚠ A VALUE IMPORT, not only a type — the payout card below needs Health's
// bucketing rule. Same layer, and `propReport.ts` imports nothing from here, so
// there is no cycle.
import { classifyPropAccount, type PropAccount } from './propReport';

/** The cost fields, which ride `extras` and have no columns. */
export interface EconomicsAccount extends PropAccount {
  feeWaived: boolean;
  feeCadence: string;
  feeAmount: number;
}

/**
 * Does this account cost REAL MONEY?
 *
 * ⚠ Cost fields exist on EVERY account, including SIM and Replay. Those cost
 * nothing real, so a fee typed onto one would silently inflate spend, depress
 * ROI and corrupt cost-per-funded — the exact numbers this tab exists to be
 * trusted on.
 *
 * ⚠ STATUS IS NOT CONSULTED. See the header: a blown account still cost money.
 */
export function isPropCostBearing(a: EconomicsAccount): boolean {
  if (!a) return false;
  if (a.excludeFromMetrics) return false;
  const t = a.accountType;
  return t === 'combine' || t === 'sim-funded' || t === 'live-funded';
}

export function economicsAccounts(
  accounts: readonly EconomicsAccount[],
): EconomicsAccount[] {
  return accounts.filter(isPropCostBearing);
}

export interface FeeBreakdown { evalFees: number; reset: number; activation: number }

export interface ResetStats {
  count: number; free: number; paid: number; spend: number;
}

export interface Pipeline {
  started: number;
  active: number;
  passed: number;
  failed: number;
  funded: number;
  freeAccounts: number;
  /** Percent 0–100, or null when no evals exist — `—`, not `0%`. */
  passRate: number | null;
  costPerFunded: number | null;
  evalsPerFunded: number | null;
  resetsPerPass: number | null;
}

export interface Economics {
  accounts: EconomicsAccount[];
  payouts: number;
  fees: number;
  feeBreakdown: FeeBreakdown;
  netCash: number;
  /**
   * Dollars back per dollar spent.
   *
   * ⚠ `null`, not 0, when nothing has been spent. "No data" and "you got nothing
   * back" are different statements and the second is an accusation.
   */
  roiPerDollar: number | null;
  breakEvenGap: number;
  resets: ResetStats;
  pipeline: Pipeline;
  recurring: { count: number; monthly: number };
  /**
   * Trade P&L across these accounts.
   *
   * ⚠ DELIBERATELY OUTSIDE every cash figure. It is account balance, not money
   * you can spend — folding it in is what made the old Capital Efficiency card
   * read better than reality.
   */
  simPnl: number;
}

/**
 * Every Economics figure, in one pass.
 *
 * ⚠ MAGNITUDES on both sides of the ledger. Fees and payouts are both stored
 * negative (money out of the firm's books), and the sign is user-editable, so
 * per-row `Math.abs` is what keeps a mistyped row from cancelling a correct one.
 * `domain/propReport.ts` carries the same note and the receipt for it: four real
 * $19 eval fees typed as −19/+19/−19/+19 once totalled $0 of fees.
 */
export function computeEconomics(
  accounts: readonly EconomicsAccount[],
  trades: readonly AccountTrade[],
): Economics {
  const accts = economicsAccounts(accounts);

  let payouts = 0;
  let evalFees = 0;
  let resetFees = 0;
  let activationFees = 0;
  let resetCount = 0;
  let freeResets = 0;

  for (const a of accts) {
    for (const adj of a.adjustments ?? []) {
      if (!adj) continue;
      const amt = Math.abs(Number(adj.amount) || 0);
      if (adj.type === 'Payout') payouts += amt;
      else if (adj.type === 'Eval fee') evalFees += amt;
      else if (adj.type === 'Activation fee') activationFees += amt;
      else if (adj.type === 'Reset fee') {
        resetFees += amt;
        resetCount++;
        // ⚠ A $0 reset is logged so the COUNT survives — free resets are a real
        // firm perk and "how many resets have I used" is the question, not "how
        // much did they cost".
        if (amt === 0) freeResets++;
      }
    }
  }

  const fees = evalFees + resetFees + activationFees;

  // ⚠ TYPE ONLY — see the header. `sim-funded` is an eval here.
  const evals = accts.filter((a) => a.accountType === 'combine' || a.accountType === 'sim-funded');
  const funded = accts.filter((a) => a.accountType === 'live-funded');
  const passed = evals.filter((a) => a.status === 'passed').length;
  const failed = evals.filter((a) => a.status === 'blown').length;
  const active = evals.filter((a) => a.status === 'active').length;

  // ⚠ Recurring burn stops at ARCHIVE, so a dead eval does not bill forever.
  // A waived fee contributes nothing by construction (`feeAmount > 0`).
  const recurring = accts.filter(
    (a) => a.feeCadence === 'monthly' && Number(a.feeAmount) > 0 && !a.archived,
  );

  const ids = new Set(accts.map((a) => a.id));
  const simPnl = trades.reduce(
    (s, t) => (t.accountId && ids.has(t.accountId) ? s + (t.netPnl ?? 0) : s), 0,
  );

  return {
    accounts: accts,
    payouts,
    fees,
    feeBreakdown: { evalFees, reset: resetFees, activation: activationFees },
    netCash: payouts - fees,
    roiPerDollar: fees > 0 ? payouts / fees : null,
    breakEvenGap: Math.max(0, fees - payouts),
    resets: {
      count: resetCount, free: freeResets, paid: resetCount - freeResets, spend: resetFees,
    },
    pipeline: {
      started: evals.length,
      active,
      passed,
      failed,
      funded: funded.length,
      freeAccounts: accts.filter((a) => a.feeWaived === true).length,
      // ⚠ Pass rate counts ACCOUNTS, never attempts. Resets belong to the eval
      // they are on, so five resets on one eval is still one eval.
      passRate: evals.length ? (passed / evals.length) * 100 : null,
      costPerFunded: funded.length ? fees / funded.length : null,
      evalsPerFunded: funded.length ? evals.length / funded.length : null,
      resetsPerPass: passed ? resetCount / passed : null,
    },
    recurring: {
      count: recurring.length,
      monthly: recurring.reduce((s, a) => s + Number(a.feeAmount), 0),
    },
    simPnl,
  };
}

// ---------------------------------------------------------------------------
// Per-firm rollup
// ---------------------------------------------------------------------------

export interface FirmRow {
  firm: string;
  spend: number;
  payouts: number;
  evals: number;
  passed: number;
  funded: number;
  net: number;
  passRate: number | null;
}

/**
 * Group by firm. Grouping only — every eligibility decision was made above.
 *
 * ⚠ The key is CASE-INSENSITIVE. Firm is free text typed per account, so the
 * same firm arrives as "Lucid" and "LUCID" and would otherwise split into two
 * rows that each look like a separate firm with half the spend. The DISPLAY
 * keeps the first spelling seen rather than forcing a case, because firms style
 * their own names — MyFundedFutures, TopstepX.
 *
 * ⚠ A blank firm becomes `Unspecified` rather than being dropped. The spend was
 * still real, and a row missing from a comparison table is how a total stops
 * adding up.
 */
export function economicsByFirm(e: Economics): FirmRow[] {
  const rows = new Map<string, Omit<FirmRow, 'net' | 'passRate'>>();

  for (const a of e.accounts) {
    const firm = (a.firm || 'Unspecified').trim() || 'Unspecified';
    const key = firm.toLowerCase();
    let r = rows.get(key);
    if (!r) {
      r = { firm, spend: 0, payouts: 0, evals: 0, passed: 0, funded: 0 };
      rows.set(key, r);
    }
    for (const adj of a.adjustments ?? []) {
      if (!adj) continue;
      const amt = Math.abs(Number(adj.amount) || 0);
      if (adj.type === 'Payout') r.payouts += amt;
      else if (adj.type === 'Eval fee' || adj.type === 'Reset fee' || adj.type === 'Activation fee') {
        r.spend += amt;
      }
    }
    if (a.accountType === 'live-funded') r.funded++;
    else {
      r.evals++;
      if (a.status === 'passed') r.passed++;
    }
  }

  return [...rows.values()]
    .map((r) => ({
      ...r,
      net: r.payouts - r.spend,
      passRate: r.evals ? (r.passed / r.evals) * 100 : null,
    }))
    // Best net first — the firms that actually paid you head the table.
    .sort((a, b) => b.net - a.net);
}

// ---------------------------------------------------------------------------
// Rebill risk
// ---------------------------------------------------------------------------

export interface RebillRisk {
  accountId: string;
  name: string;
  firm: string;
  idleDays: number;
  /** Null when no eval fee has ever been logged, so nothing anchors the cycle. */
  daysToRebill: number | null;
  amount: number;
}

const dayMs = 86400000;
const asDate = (iso: string) => new Date(`${iso}T00:00:00Z`);

/**
 * Recurring accounts that look dormant.
 *
 * ⚠ Framed as a DEADLINE, not a status: "you get rebilled in 4 days" is
 * actionable where "no trades in 19 days" is trivia.
 *
 * ⚠ The rebill date is INFERRED — last eval fee plus whole months until it lands
 * in the future — because firms do not tell us their billing day. It must be
 * presented as an estimate, never as fact.
 *
 * ⚠ 14 days idle is the threshold, and an account with NO trades at all has
 * `idleDays == null` and is skipped. That is beta's behaviour and it is arguably
 * wrong — a recurring account that has never traded is the strongest rebill
 * candidate there is — but changing it would surface accounts beta stays silent
 * about, so it is matched and flagged rather than quietly improved.
 */
export function economicsRebillRisk(
  e: Economics, trades: readonly AccountTrade[], todayIso: string,
): RebillRisk[] {
  const out: RebillRisk[] = [];

  for (const a of e.accounts) {
    if (a.feeCadence !== 'monthly' || !(Number(a.feeAmount) > 0)) continue;
    if (a.archived) continue;

    const fees = (a.adjustments ?? [])
      .filter((x) => x && x.type === 'Eval fee' && x.date)
      .map((x) => x.date as string)
      .sort();
    const lastFee = fees.length ? fees[fees.length - 1] : null;

    const mine = trades
      .filter((t) => t.accountId === a.id && t.date)
      .map((t) => t.date as string)
      .sort();
    const lastTrade = mine.length ? mine[mine.length - 1] : null;
    if (!lastTrade) continue;

    const idleDays = Math.round((asDate(todayIso).getTime() - asDate(lastTrade).getTime()) / dayMs);
    if (idleDays < 14) continue;

    let daysToRebill: number | null = null;
    if (lastFee) {
      const d = asDate(lastFee);
      const now = asDate(todayIso);
      // Roll forward a month at a time until it lands in the future.
      while (d < now) d.setUTCMonth(d.getUTCMonth() + 1);
      daysToRebill = Math.round((d.getTime() - now.getTime()) / dayMs);
    }

    out.push({
      accountId: a.id, name: a.name, firm: a.firm,
      idleDays, daysToRebill, amount: Number(a.feeAmount),
    });
  }

  // Soonest rebill first. A null date sorts last — it is the least urgent
  // because nothing anchors its cycle.
  return out.sort((x, y) => (x.daysToRebill ?? 99) - (y.daysToRebill ?? 99));
}


// ===========================================================================
// PAYOUT VELOCITY — the fifth Economics card, and the only forward-looking one.
// ---------------------------------------------------------------------------
// "How often does money actually arrive, and how fast?" Everything else on this
// tab is a cost that has already been paid.
//
// ⚠ IT READS `Payout` ADJUSTMENTS, WHICH ARE THE ONLY RECORD OF CASH ARRIVING.
// A funded account's balance going up is not a payout — it is an account
// balance, which is the distinction the Sim-P&L aside on the card above exists
// to make. Nothing here infers a payout from a trade.
//
// ⚠ On Josh's book there are ZERO payout adjustments, so every figure below is
// null and the card renders its empty state. That is beta's behaviour too, and
// it is the honest reading: he has spent $310 and been paid nothing.
//
// 🚨 AND ITS POPULATION IS NOT THIS TAB'S — it is HEALTH'S. Funded + eval only,
// with STATUS beating type, so a payout or a reset fee on an account that is
// blown, closed, passed or paused is counted by the four COST cards above and
// NOT by this one. That is two populations on one tab. It is beta's, and the
// file header forty lines up says "STATUS IS NOT CONSULTED", which is true of
// everything here EXCEPT this card. See the filter itself for the citations.
// ===========================================================================

export interface PayoutVelocity {
  /** Payouts dated on or after `ytdStart`. */
  ytdTotal: number;
  ytdCount: number;
  /** Null rather than 0 when nothing was received — `—`, not `$0`. */
  avgPayout: number | null;
  /**
   * Average days between the most recent payouts.
   *
   * ⚠ ALL-TIME, not year-to-date, and beta is the same. A cadence needs two
   * payouts to exist at all, and a trader paid in December and February has a
   * real 60-day cadence that a YTD window would report as "not enough data".
   */
  cadenceDays: number | null;
  /** `(30 / cadence) x avgPayout`, or null when either input is missing. */
  projected30: number | null;
  /** YTD payouts ÷ YTD reset spend. Null when no reset fees are logged. */
  payoutToReset: number | null;
  /** YTD reset spend, so the sub-line can name it. */
  resetSpendYtd: number;
  /** Every payout ever, oldest first — the cumulative sparkline's series. */
  all: { date: string; amount: number }[];
}

/**
 * @param ytdStart inclusive ISO date
 * @param ytdEnd   inclusive ISO date
 */
export function payoutVelocity(
  accounts: readonly EconomicsAccount[],
  ytdStart: string,
  ytdEnd: string,
): PayoutVelocity {
  // ── 🚨 BETA'S FILTER FOR THIS CARD, NOT THE TAB'S ──
  //
  // Beta builds this card inside `renderPropReport` from `propAccts` — funded +
  // eval by `classifyPropAccount` (app/index.html:40700-40706), used for the payout
  // list at :40748 and the reset spend at :40777 — and only then drops it at
  // the foot of the Economics tab (:41188). So the card renders under Economics
  // and is computed from Health's set.
  //
  // 🔴 THE PORT INHERITED THE NEIGHBOURING DEFAULT INSTEAD, and it is wrong in
  // BOTH directions: it counted payouts and reset fees on accounts that are
  // blown, closed, passed or paused (which beta hides), and it dropped accounts
  // whose legacy `accountType` is a plain `'funded'` (which beta still buckets as
  // funded, propReport.ts:79). One filter closes both.
  //
  // ⚠ ARCHIVED ACCOUNTS STAY IN. Nothing in beta's filter reads `archived`, and
  // archiving is de-clutter — money that was actually paid out does not stop
  // having been paid because the account was tidied away.
  //
  // ⚠ `excludeFromMetrics` IS STILL HONOURED, and it has to be checked here:
  // `classifyPropAccount` does not read it. Beta drops those accounts one step
  // earlier (app/index.html:40675), before bucketing. A SIM account with a
  // "payout" typed on it is not money.
  const accts = accounts.filter((a) => {
    if (!a || a.excludeFromMetrics) return false;
    const bucket = classifyPropAccount(a);
    return bucket === 'funded' || bucket === 'eval';
  });

  const all: { date: string; amount: number }[] = [];
  let resetSpendYtd = 0;

  for (const a of accts) {
    for (const adj of a.adjustments ?? []) {
      if (!adj) continue;
      const amt = Math.abs(Number(adj.amount) || 0);
      // ⚠ A payout with no DATE is dropped from this card and only this card.
      // It still counts toward the cash position above, because "how much have
      // I been paid" survives a missing date and "how often" cannot.
      if (adj.type === 'Payout' && adj.date) all.push({ date: adj.date, amount: amt });
      else if (adj.type === 'Reset fee' && adj.date
        && adj.date >= ytdStart && adj.date <= ytdEnd) resetSpendYtd += amt;
    }
  }

  all.sort((x, y) => x.date.localeCompare(y.date));

  const ytd = all.filter((p) => p.date >= ytdStart && p.date <= ytdEnd);
  const ytdTotal = ytd.reduce((s, p) => s + p.amount, 0);
  const ytdCount = ytd.length;
  const avgPayout = ytdCount ? ytdTotal / ytdCount : null;

  // ⚠ THE LAST SIX, not all of them — beta's window, and it is the right one.
  // A trader whose firm moved from monthly to fortnightly has a current cadence
  // that a lifetime average would take a year to catch up with.
  let cadenceDays: number | null = null;
  if (all.length >= 2) {
    const recent = all.slice(-Math.min(6, all.length));
    let sum = 0;
    for (let i = 1; i < recent.length; i++) {
      sum += Math.max(0, daysBetween(recent[i - 1]!.date, recent[i]!.date));
    }
    cadenceDays = sum / (recent.length - 1);
  }

  // ⚠ `cadenceDays > 0` guards the division AND a real case: two payouts on the
  // same day give a cadence of 0, which would project an infinite month.
  const projected30 = cadenceDays != null && cadenceDays > 0 && avgPayout != null
    ? (30 / cadenceDays) * avgPayout
    : null;

  return {
    ytdTotal,
    ytdCount,
    avgPayout,
    cadenceDays,
    projected30,
    payoutToReset: resetSpendYtd > 0 ? ytdTotal / resetSpendYtd : null,
    resetSpendYtd,
    all,
  };
}

/**
 * Whole days from one ISO date to another.
 *
 * ⚠ `Date.UTC`, not `new Date(string)` — the same reason as `domain/propTilt`.
 * A local parse makes the day either side of a DST change 23 or 25 hours long,
 * so a fortnightly cadence measured across March comes out as 13.96 days and
 * rounds to 14 by luck rather than by arithmetic.
 */
function daysBetween(a: string, b: string): number {
  const pa = /^(\d{4})-(\d{2})-(\d{2})/.exec(a);
  const pb = /^(\d{4})-(\d{2})-(\d{2})/.exec(b);
  if (!pa || !pb) return 0;
  const ta = Date.UTC(Number(pa[1]), Number(pa[2]) - 1, Number(pa[3]));
  const tb = Date.UTC(Number(pb[1]), Number(pb[2]) - 1, Number(pb[3]));
  return (tb - ta) / 86_400_000;
}
